import {
  Tag,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { CustomerUser } from '@texas/api/endpoints/customerUsersApi';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { ArchivedTooltip } from '@texas/components/shared/tooltip/ArchivedTooltip';
import { TFunction } from 'i18next';

export const externalUsersColumns = (
  t: TFunction,
  onOpenCustomType: (v: { id: number; customType: string }) => void,
  onArchiveOpen: (v: { id: number; email: string }) => void,
  onRestore: (user: CustomerUser) => Promise<void>,
  onOpenSetMain: (v: { id: number; name: string }) => void,
) => {
  const columnHelper = createColumnHelper<CustomerUser>();
  const columns = [
    columnHelper.accessor('userName', {
      cell: (info) => (
        <Flex gap={4}>
          <Text>{info.getValue()}</Text>
          {info.row.original.isMain && (
            <Tag
              variant="colored"
              size="sm"
              textTransform="uppercase"
              fontWeight="bold"
            >
              {t('general.main')}
            </Tag>
          )}
        </Flex>
      ),
      header: t('general.name'),
    }),
    columnHelper.accessor('email', {
      cell: (info) => info.getValue(),
      header: t('general.email'),
    }),
    columnHelper.accessor('customUserType', {
      cell: (info) => info.getValue(),
      header: t('auth.role'),
    }),
    columnHelper.accessor('lastLogin', {
      cell: (info) => info.getValue(),
      header: t('customerUser.lastLogin'),
    }),
    columnHelper.accessor('id', {
      cell: (info) => (
        <Flex justifyContent="end" alignItems="center" gap={2}>
          {info.row.original.userArchived && (
            <ArchivedTooltip archived={info.row.original.userArchived} />
          )}
          <Menu placement="left">
            <MenuButton float="right" aria-label={t('general.more')}>
              <Icons.DotsHorizontal boxSize={6} />
            </MenuButton>
            <Portal>
              <MenuList color="white">
                {info.row.original.userArchived ? (
                  <MenuItem
                    icon={<Icons.Undo boxSize={defaultIconSize} />}
                    aria-label={t('general.restore')}
                    onClick={() => onRestore(info.row.original)}
                  >
                    {t('general.restore')}
                  </MenuItem>
                ) : (
                  <>
                    <MenuItem
                      icon={<Icons.Pencil boxSize={defaultIconSize} />}
                      aria-label={t('customerUser.changeCustomRole')}
                      onClick={() =>
                        onOpenCustomType({
                          id: info.row.original.id,
                          customType: info.row.original.customUserType ?? '',
                        })
                      }
                    >
                      {t('customerUser.changeCustomRole')}
                    </MenuItem>
                    {!info.row.original.isMain && (
                      <MenuItem
                        icon={<Icons.Flag boxSize={defaultIconSize} />}
                        aria-label={t('customerUser.setMain')}
                        onClick={() =>
                          onOpenSetMain({
                            id: info.row.original.id,
                            name: info.row.original.userName,
                          })
                        }
                      >
                        {t('customerUser.setMain')}
                      </MenuItem>
                    )}
                    <MenuItem
                      icon={<Icons.TrashOutline boxSize={defaultIconSize} />}
                      aria-label={t('general.archive')}
                      onClick={() =>
                        onArchiveOpen({
                          id: info.row.original.userId,
                          email: info.row.original.email,
                        })
                      }
                    >
                      {t('general.archive')}
                    </MenuItem>
                  </>
                )}
              </MenuList>
            </Portal>
          </Menu>
        </Flex>
      ),
      header: '',
      enableSorting: false,
      maxSize: 100,
    }),
  ];

  return columns;
};
